import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarMonths, parse } from "date-fns";
import { generatePath, Link } from "react-router-dom";

import Hint from "~/components/Hint/Hint";
import type { HintThemeVariants } from "~/components/Hint/hintVariants.ts";
import { TIME_TRACKINGS_PATH } from "~/constants/appRoutes.ts";
import { monthlyClosingQueries } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import { getMonthNameFromNumber } from "~/utils/dateAndTimeUtils.ts";

type Props = {
	userId: string;
};

const reminderSteps: { theme: HintThemeVariants; text: (monthName: string) => string }[] = [
	{ theme: "info", text: (monthName) => `Du hast den Monat ${monthName} noch nicht geschlossen.` },
	{ theme: "warning", text: (monthName) => `Du hast den Monat ${monthName} immer noch nicht geschlossen. Bitte schließe ihn.` },
	{ theme: "error", text: (monthName) => `Der Monat ${monthName} ist seit langem offen. Du musst ihn jetzt schließen.` },
];

/**
 * The full close of an earlier month is enforced only on the screen: the backend refuses no tracking
 * because an earlier month is open. The reminder gets more insistent the older the open month is.
 * The backend lists only the months that fall in an employment period of the user or in which a
 * staffing needs a closing. The text addresses the user with "du", so the view shows it on the own page only.
 */
export function OpenEarlierMonthsReminder(props: Props) {
	const { data } = useQuery(monthlyClosingQueries.openMonths(props.userId));

	const today = new Date();
	const openMonths = (data?.months ?? []).map((key) => {
		const date = parse(key, "yyyy-MM", today);
		return { age: differenceInCalendarMonths(today, date), date, key };
	});
	if (openMonths.length === 0) {
		return null;
	}

	return (
		<div className="mb-6 flex flex-col gap-2">
			{openMonths.map((month) => {
				const step = reminderSteps[Math.min(Math.max(month.age, 1), reminderSteps.length) - 1];
				const monthName = `${getMonthNameFromNumber(month.date.getMonth())} ${month.date.getFullYear()}`;
				const path = `${generatePath(TIME_TRACKINGS_PATH, { userId: props.userId })}?year=${month.date.getFullYear()}&month=${month.date.getMonth() + 1}`;
				return (
					<Hint key={month.key}
						  theme={step.theme}
						  size="sm">
						{step.text(monthName)}{" "}
						<Link to={path}
							  className="font-semibold underline">
							Zum Monat
						</Link>
					</Hint>
				);
			})}
		</div>
	);
}
